import { useState, useEffect } from 'react';
import type { CampaignProduct } from '@bvengineering/cas-member-gateway-service-client';
import { MS_PER_SECOND, MS_PER_MINUTE, PRODUCT_STATE } from '@/utils';

export const RESERVATION_MS = 15 * MS_PER_MINUTE;

function getExpiryTime(startIso: string): number | null {
  const start = new Date(startIso).getTime();
  return Number.isNaN(start) ? null : start + RESERVATION_MS;
}

export function hasActiveReservation(product: CampaignProduct | null | undefined): boolean {
  const expiryTime = getExpiryTime(product?.reservedDate ?? '');
  return (
    product?.state?.toLowerCase() === PRODUCT_STATE.RESERVED &&
    !product.claimedDate &&
    (expiryTime === null || Date.now() < expiryTime)
  );
}

function getSecondsRemaining(startIso: string): number {
  const expiryTime = getExpiryTime(startIso);
  if (expiryTime === null) return 0;
  return Math.max(0, Math.ceil((expiryTime - Date.now()) / MS_PER_SECOND));
}

export function useReservationTimer(startIso: string): {
  secondsLeft: number;
  hasExpired: boolean;
} {
  const [secondsLeft, setSecondsLeft] = useState(() => getSecondsRemaining(startIso));

  useEffect(() => {
    const initial = getSecondsRemaining(startIso);
    setSecondsLeft(initial);
    if (initial <= 0) return;
    const id = setInterval(() => {
      const next = getSecondsRemaining(startIso);
      setSecondsLeft(next);
      if (next <= 0) clearInterval(id);
    }, MS_PER_SECOND);
    return () => clearInterval(id);
  }, [startIso]);

  const expiryTime = getExpiryTime(startIso);
  const hasExpired = expiryTime !== null && Date.now() >= expiryTime;
  return { secondsLeft, hasExpired };
}

export function useReservationExpiry(startIso: string): boolean {
  const expiryTime = getExpiryTime(startIso);
  const [elapsedExpiryTime, setElapsedExpiryTime] = useState<number | null>(null);

  useEffect(() => {
    if (expiryTime === null) return;

    const remaining = expiryTime - Date.now();
    if (remaining <= 0) return;

    const id = setTimeout(() => setElapsedExpiryTime(expiryTime), remaining);
    return () => clearTimeout(id);
  }, [expiryTime]);

  return expiryTime !== null && (elapsedExpiryTime === expiryTime || Date.now() >= expiryTime);
}
